
/**
 * Language resource for text content.
 * @typedef {Object} TextContent
 * @property {string} submit - Label for the submit button.
 * @property {string} reset - Label for the reset button.
 * @property {string} emailId - Label for the email ID field.
 * @property {string} loginOrCreate - Text for indicating login or account creation.
 * @property {string} verification - Text for verification.
 * @property {string} verify - Label for the verify button.
 * @property {string} firstName - Label for the first name field.
 * @property {string} lastName - Label for the last name field.
 * @property {string} phoneNumber - Label for the phone number field.
 * @property {string} cancel - Label for the cancel button.
 * @property {string} displayName - Label for the display name field.
 * @property {string} fullName - Label for the full name field.
 * @property {string} email - Label for the email field.
 * @property {string} edit - Label for the edit action.
 * @property {string} joined - Label indicating joined date.
 * @property {string} join - Label for the join action.
 * @property {string} createNewLoop - Text for creating a new loop.
 * @property {string} skip - Label for the skip action.
 * @property {string} next - Label for the next action.
 * @property {string} status - Label for the status field.
 * @property {string} paymentStatus - Label for the paymentStatus field.
 * @property {string} OrderId - Label for the OrderId field.
 * @property {string} action - Label for the action field.
 * @property {string} sno - Label for the serial number field.
 * @property {string} areYouSure - Confirmation message prefix.
 * @property {string} yes - Label for the yes option in a confirmation message.
 * @property {string} no - Label for the no option in a confirmation message.
 * @property {string} dashboard - Label for the dashboard.
 * @property {string} view - Label for the view action.
 * @property {string} deActivate - Label for the deactivate action.
 * @property {string} activate - Label for the activate action.
 * @property {string} lastLogin - Label for the last login field.
 * @property {string} createdAt - Label for the created at field.
 * @property {string} editProfile - Label for the edit profile action.
 * @property {string} saveChanges - Label for the save changes action.
 * @property {string} continue - Label for the continue action.
 * @property {string} logout - Label for the logout action.
 * @property {string} update - Label for the update action.
 * @property {string} add - Label for the add action.
 * @property {string} delete - Label for the delete action.
 * @property {string} date - Label for the date field.
 * @property {string} to - Label for indicating 'to'.
 * @property {string} roles - Label for the roles field.
 * @property {string} filter - Label for the filter action.
 * @property {string} noRecordFound - Message indicating no records found.
 * @property {string} imageOnlyMessgae - Message indicating allowed image formats.
 * @property {string} imageSizeExceedingMessage - Message indicating image size limit.
 * @property {string} wantToLogout - Message for logout confirmation.
 * @property {string} areYouSureLogout - Logout confirmation message prefix.
 * @property {string} changePhoto - Label for changing photo action.
 * @property {string} viewProfile - Label for viewing profile action.
 * @property {string} signOut - Label for the sign out action.
 * @property {string} name - Label for the name field.
 * @property {string} readMore - Label for the read more action.
 * @property {string} active - Label for the active status.
 * @property {string} warning - Label for the warning.
 * @property {string} enterVerificationCode - Label for entering verification code.
 * @property {string} successfull - Success message indicating successful action.
 * @property {string} successfully - Success message indicating successful action.
 * @property {string} okay - Label for the okay action.
 * @property {string} features - Label for the features section.
 * @property {string} switch - Label for the switch action.
 * @property {string} back - Label for the back action.
 * @property {string} serialNo - Label for the serial number field.
 * @property {string} loadMore - Label for loading more data action.
 * @property {string} type - Label for the type field.
 * @property {string} confirm - Label for the confirm action.
 * @property {string} save - Label for the save action.
 * @property {string} mobileNumber - Label for the mobile number field.
 * @property {string} pin - Label for the pin field.
 * @property {string} unPinMessageText - Label for the unpin message action.
 * @property {string} pinMessageText - Label for the pin message action.
 * @property {string} imageTooltip - Tooltip for the image.
 */

/**
 * Language resource for text content.
 * @type {TextContent}
 */
export const text = {
  submit: "Submit",
  reset: "Reset",
  emailId: "Email id",
  loginOrCreate: "Login or Create Account",
  verification: "Verification",
  verify: "Verify",
  firstName: "First Name",
  lastName: "Last Name",
  phoneNumber: "Phone Number",
  cancel: "Cancel",
  displayName: "Display Name",
  fullName: "Full Name",
  email: "Email",
  edit: "Edit",
  joined: "Joined",
  join: "Join",
  createNewLoop: "Create a new Loop ",
  skip: "Skip",
  next: "Next",
  status: "Status",
  paymentStatus: "Payment Status",
  action: "Action",
  sno: "S.No.",
  areYouSure: "Are you sure",
  yes: "Yes",
  no: "No",
  dashboard: "dashboard",
  view: "View",
  deActivate: "Deactivate",
  activate: "Activate",
  lastLogin: "Last Login",
  createdAt: "Created At",
  editProfile: "Edit Profile",
  saveChanges: "Save Changes",
  continue: "Continue",
  logout: "Logout",
  update: "Update",
  add: "Add",
  delete: "Delete",
  date: "Date",
  to: "To",
  roles: "Roles",
  filter: "Filter",
  noRecordFound: "No Record Found",
  imageOnlyMessgae: "Please add either a JPEG, JPG or a PNG file",
  imageSizeExceedingMessage: "Please add a picture not exceeding 5MBs",
  wantToLogout: "Logout",
  areYouSureLogout: "Are you sure want to logout?",
  changePhoto: "Change Photo",
  viewProfile: "View Profile",
  signOut: "Sign out",
  name: "Name",
  readMore: "Read More",
  active: "Active",
  warning: "warning",
  enterVerificationCode: "Enter Verification Code✍️",
  successfull: "Successfull",
  successfully: "Successfully",
  okay: "Okay",
  features: "Features",
  switch: "Switch",
  back: "Back",
  serialNo: "Serial No",
  loadMore: "Load More",
  type: "Type",
  confirm: "Confirm",
  save: "Save",
  mobileNumber: "Mobile",
  pin: "Pin",
  unPinMessageText: "Unpin Message",
  pinMessageText: "Pin Message",
  imageTooltip: "Image Tooltip",
  generateSummary: "Generate Summary",
  notification: "Notification",
  enterDescription: "Enter Description",
  enterMessage: "Enter Message",
  countryCode: "Country Code",
  orderId: "Order Id",
  product: "Product",
  transactionId: "Transaction Id", 
  startDate: "Start Date",
  endDate: "End Date",
  amount: "Amount", 
  transactionDate: "Transaction Date",
  store: "Store",
  transactionType: "Transaction Type",
  userNutrients: "User Nutrients Logs",
};
